import { Formik, FormikProps } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { mutate } from 'swr'

import { AdaptiveModal } from '../common/AdaptiveModal'
import { backendFetchResult, Result } from '../common/api'
import {
  DialogActivator,
  gidToId,
  ResultBanner,
  ResultToast,
  useDialogActivator,
} from '../common/helpers'
import { SubscriptionContract } from '../common/subscription-models'
import { AddressesModal } from './AddressesModal'
import { Form } from './Form'
import { CustomerAddress } from './models'
import { DeliveryDetailsForm, deliveryDetailsToFormData } from './models-form'
import { deliveryDetailsFormDataToInput, DeliveryDetailsInput } from './models-input'

export const updateDeliveryDetails = async (
  scGid: string,
  deliveryDetailsInput: DeliveryDetailsInput
): Promise<Result<any>> => {
  const url = `/subscriptions/${gidToId(scGid)}`
  const body = JSON.stringify({
    input: deliveryDetailsInput,
  })
  const data = (await backendFetchResult('PUT', url, { body })) as Result<any>

  if (data.status === 'success') {
    mutate(['subscription-contract', `${gidToId(scGid)}`])
  }

  return data
}
interface Props {
  sc: SubscriptionContract
  activator: DialogActivator
}

export const DeliveryDetailsEditDialog = (props: Props) => {
  const [saveResponse, setSaveResponse] = useState<Result<any> | null>(null)
  const addressesModalActivator = useDialogActivator()

  const initialData = deliveryDetailsToFormData(
    props.sc.gql_data.deliveryMethod,
    props.sc.gql_data.deliveryPrice
  )

  useEffect(() => {
    setSaveResponse(null)
  }, [props.activator.showCounter])

  const handleAddressSelectOpen = useCallback(() => {
    props.activator.close()
    addressesModalActivator.show()
  }, [addressesModalActivator, props.activator])

  const handleSubmit = useCallback(
    async (deliveryDetailsForm: DeliveryDetailsForm, actions: any) => {
      actions.setSubmitting(true)
      const res = await updateDeliveryDetails(
        props.sc.gid,
        deliveryDetailsFormDataToInput(deliveryDetailsForm)
      )

      if (res.status === 'error' && res.message && res.field) {
        actions.setFieldError(res.field, res.message)
        res.field = undefined
        res.message = 'Please fix errors below and retry submitting the form'
      }

      setSaveResponse(res)
      actions.setSubmitting(false)

      if (res.status === 'success') {
        props.activator.close()
      }
    },
    [props]
  )

  const handleAddressSelect = (
    formik: FormikProps<DeliveryDetailsForm>,
    address?: CustomerAddress
  ) => {
    if (address) {
      formik.setFieldValue('address.address1', address.address1)
      formik.setFieldValue('address.address2', address.address2)
      formik.setFieldValue('address.company', address.company)
      formik.setFieldValue('address.city', address.city)
      formik.setFieldValue('address.countryCode', address.countryCode)
      formik.setFieldValue('address.firstName', address.firstName)
      formik.setFieldValue('address.lastName', address.lastName)
      formik.setFieldValue('address.phone', address.phone)
      formik.setFieldValue('address.provinceCode', address.provinceCode)
      formik.setFieldValue('address.province', address.province)
      formik.setFieldValue('address.zip', address.zip)
    }
    addressesModalActivator.close()
    props.activator.show()
  }

  return (
    <>
      <Formik initialValues={initialData} onSubmit={handleSubmit} enableReinitialize>
        {(formik) => (
          <>
            <AdaptiveModal
              open={props.activator.open}
              onClose={() => {
                formik.resetForm()
                props.activator.close()
              }}
              title="Edit delivery details"
              primaryAction={{
                content: 'Save',
                loading: formik.isSubmitting,
                onAction: formik.submitForm,
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  disabled: formik.isSubmitting,
                  onAction: () => {
                    formik.resetForm()
                    props.activator.close()
                  },
                },
              ]}
            >
              <AdaptiveModal.Section>
                <ResultBanner result={saveResponse} setResult={setSaveResponse} />
                <Form
                  currencyCode={props.sc.gql_data.currencyCode}
                  onAddressSelect={handleAddressSelectOpen}
                />
              </AdaptiveModal.Section>
            </AdaptiveModal>
            <AddressesModal
              activator={addressesModalActivator}
              customerGid={props.sc.gql_customer_gid}
              onComplete={(address) => handleAddressSelect(formik, address)}
            />
          </>
        )}
      </Formik>
      <ResultToast result={saveResponse} setResult={setSaveResponse} />
    </>
  )
}
